import React, { useState } from 'react';

import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { useOnComponentMount } from '../../hooks/use-on-component-mount';
import { LogService } from '../../services/log-service';
import { getTimezones } from './api';
import messages from './messages';
import style from './style.module.less';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';

let cachedTimeZones = [];

const elementId = 'time-zone-dropdown';

const TimeZoneDropdown = ({
  onTimeZoneSelected,
  selectedTimeZone,
  className = '',
  labelClassName = '',
  dropdownClassName = '',
  hasError = false,
  shouldUseOnlyLegacySupportedTimeZones = false,
  excludeLabel = false,
  variant,
}) => {
  const [timeZones, setTimeZones] = useState(cachedTimeZones);

  useOnComponentMount(() => {
    if (!cachedTimeZones.length) {
      const fetchTimeZones = async () => {
        try {
          const timezonesResponse = await getTimezones(shouldUseOnlyLegacySupportedTimeZones);
          cachedTimeZones = timezonesResponse;
          setTimeZones(timezonesResponse);
        } catch (error) {
          LogService.logError(new Error('Error fetching time zones', { cause: error }));
          setTimeZones([]);
        }
      };
      fetchTimeZones();
    }
  });

  const selectedTimeZoneObject = timeZones.find((timeZone) => timeZone.id === selectedTimeZone);

  const combinedLabelClassNames = classnames(style['time-zone-label'], labelClassName, {
    [style['has-error']]: hasError,
  });

  return (
    <div className={classnames(style['time-zone-group'], className)}>
      {!excludeLabel && (
        <label className={combinedLabelClassNames} htmlFor={elementId}>
          <FormattedMessage {...messages.timeZone} />
        </label>
      )}
      <DropdownMenu
        className={classnames(dropdownClassName, style['time-zone-dropdown'])}
        dropdownMenuShouldHaveWidthOfButton
        id={elementId}
        variant={variant}
      >
        <DropdownMenuToggler hasError={hasError} theme='white'>
          {(selectedTimeZoneObject && `${selectedTimeZoneObject?.id} (${selectedTimeZoneObject.utcOffset})`) || (
            <FormattedMessage {...messages.selectTimeZone} />
          )}
        </DropdownMenuToggler>
        {timeZones?.map((option) => {
          return (
            <DropdownMenuItem
              key={option.id}
              onClick={() => onTimeZoneSelected(option)}
              selected={selectedTimeZoneObject?.id === option.id}
            >
              {`${option.id} (${option.utcOffset})`}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenu>
    </div>
  );
};

TimeZoneDropdown.propTypes = {
  onTimeZoneSelected: PropTypes.func.isRequired,
  selectedTimeZone: PropTypes.string,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  hasError: PropTypes.bool,
  shouldUseOnlyLegacySupportedTimeZones: PropTypes.bool,
  excludeLabel: PropTypes.bool,
  variant: PropTypes.string,
};

export default TimeZoneDropdown;
